import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { IGameRequest } from '@interfaces/IGameRequest';
import { useStore } from 'effector-react';
import gamesRequest, { gameExist } from '@stores/gamesRequest/gamesRequest';
import { Form, Modal, Spin, Checkbox } from 'antd';
import { closeModal } from '@stores/modals';
import GameRequestModalPreview from '@pages/GamesPage/GameRequestModal/GameRequestModalPreview';
import { layout } from './GameRequesrModal.constants';
import DebounceSelect from '@fields/DebounceSelect/DebounceSelect';
import { fetchGameList } from '@pages/FeaturingPage/FeaturingEditModal/FeaturingEditModal.helpers';
import useTranslate from '@hooks/useTranslate';
import componentContent from '../GamesPage.module.json';
import GameCardCell from '@components/GameCard/GameCardCell';
import { GameValue } from '@pages/FeaturingPage/FeaturingEditModal/FeaturingEditModal.types';
import css from './GameRequestModalPreview.module.less';

interface GameRequestModalGameExistProps {
  games: IGameRequest[];
}

const GameRequestModalGameExist: FunctionComponent<GameRequestModalGameExistProps> = ({
  games: data,
}) => {
  const [form] = Form.useForm();
  const [selectGame, setSelectGame] = useState<GameValue | null>();
  const {
    preloaders: { gameExist: preloaders },
  } = useStore(gamesRequest);
  const content = useTranslate(componentContent);
  const [isUseSynonym, setIsUseSynonym] = useState<boolean>(false);

  const [games, setGames] = useState(data);
  useEffect(() => {
    setGames(data);
  }, [data]);

  const searchGame = useCallback(async () => {
    const foundGames = await fetchGameList(games[0].name);
    const game = foundGames.find(
      (game) => game.label.toLocaleLowerCase() === games[0].name.toLocaleLowerCase(),
    );
    if (game) {
      form.setFieldsValue({ game });
      setSelectGame(game);
    } else if (foundGames.length > 0) {
      form.setFieldsValue({ game: foundGames[0] });
      setSelectGame(foundGames[0]);
    }
  }, [games, form]);

  const selectedGame = useCallback((value, option) => {
    setSelectGame(option);
  }, []);

  useEffect(() => {
    searchGame();
  }, [searchGame]);

  const handleReject = useCallback(() => {
    form.submit();
  }, [form]);

  const submit = useCallback(
    (value) => {
      gameExist({
        suggestIds: games.map((game) => game.id),
        duplicateGameId: value.game.value,
        isUseSynonym,
      });
    },
    [games, isUseSynonym],
  );

  const handleDeleteGame = useCallback((id) => {
    setGames((oldGames) => {
      return oldGames.filter((game) => game.id !== id);
    });
  }, []);

  return (
    <Modal
      title={content.select_game}
      onCancel={() => closeModal({ name: 'gameRequest' })}
      visible
      onOk={handleReject}
      okText={content.game_exist}
      confirmLoading={!!preloaders}
    >
      <Spin spinning={!!preloaders}>
        <div className={css.preview}>
          <GameRequestModalPreview games={games} deleteGame={handleDeleteGame} />
        </div>
        <Form onFinish={(value) => submit(value)} form={form}>
          <Form.Item
            label={content.game}
            labelCol={layout.labelCol}
            name='game'
            rules={[
              {
                required: true,
              },
            ]}
          >
            <DebounceSelect
              fetchOptions={fetchGameList}
              showSearch={true}
              onChange={selectedGame}
            />
          </Form.Item>
        </Form>
        <Form.Item wrapperCol={{ offset: layout.labelCol.span, span: layout.wrapperCol.span }}>
          <GameCardCell
            text={selectGame?.label || ''}
            urlImg={selectGame?.image?.url || ''}
            isEmpty={!selectGame}
          />
        </Form.Item>
        <Form.Item label={content.add_synonyms} name='useSynonym'>
          <Checkbox value={isUseSynonym} onChange={() => setIsUseSynonym(!isUseSynonym)} />
        </Form.Item>
      </Spin>
    </Modal>
  );
};

export default GameRequestModalGameExist;
